import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "../../layout/AppLayout";
import { Alert, ErpSelect, ErpTable, Modal, toOptions } from "../../components/erp";
import { errorText } from "../../api/client";
import { prodDropdownsApi, productionTracksApi, soakingApi, SoakingRow, VaBreakupRow } from "./api";

const today = () => new Date().toISOString().slice(0, 10);

const NewSoakingModal: React.FC<{ trackId: string; plantId: string; vaBreakups: VaBreakupRow[]; editRow?: SoakingRow;
  onClose: () => void; onSaved: () => void }> = ({ trackId, plantId, vaBreakups, editRow, onClose, onSaved }) => {
  const dd = useQuery({ queryKey: ["prod", "dropdowns"], queryFn: prodDropdownsApi.get, staleTime: 5 * 60_000 });
  const chemicals = dd.data?.Data?.Chemical || [];
  const [v, setV] = useState({
    VABreakupID: "", SourceStation: "", SoakingDate: today(), StartTime: "", TimePeriod: "30",
    SaltKg: String(editRow?.SaltQty ?? ""), ChemicalID: String(editRow?.ChemicalID ?? ""), ChemicalQuantity: String(editRow?.ChemicalQty ?? ""),
    TubNumber: (editRow?.TubNumber || "").replace(/^A-/, ""), InCount: String(editRow?.SoakInCount ?? ""),
    InQuantity: String(editRow?.SoakInQty ?? ""), OutCount: String(editRow?.SoakOutCount ?? ""), OutQuantity: String(editRow?.SoakOutQty ?? ""),
    Remarks: editRow?.Remarks ?? "",
  });
  const [err, setErr] = useState<string | null>(null); const [saving, setSaving] = useState(false);
  const chosen = vaBreakups.find((b) => String(b.VABreakUpID) === v.VABreakupID);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setSaving(true); setErr(null);
    try {
      const r = await soakingApi.post({
        ...v, SoakingID: editRow?.BreakUpID ?? "", ArrivalID: chosen?.ArrivalID ?? editRow?.ArrivalID, VarietyID: chosen?.VarietyID ?? editRow?.VarietyID,
        VAGrade: chosen?.PackingGrade ?? editRow?.VAGrade, PlantID: plantId, TrackID: trackId,
        VABreakupID: chosen?.VABreakUpID ?? editRow?.BreakUpID,
      });
      if (r.Error) setErr(r.Error); else onSaved();
    } catch (ex) { setErr(errorText(ex)); } finally { setSaving(false); }
  };
  return (
    <Modal open title={editRow ? "Edit Soaking" : "New Soaking Entry"} onClose={onClose} testId="soaking-modal">
      <form onSubmit={submit} data-testid="soaking-form">
        {!editRow && (
          <div className="mb-3"><label className="form-label">Value Addition Breakup (balance)</label>
            <ErpSelect id="soaking-va-breakup" value={v.VABreakupID} onChange={(val) => setV((s) => ({ ...s, VABreakupID: val }))}
              options={vaBreakups.map((b) => ({ value: String(b.VABreakUpID), label: `${b.Variety || b.VarietyID} — ${b.PackingGrade} — ${b.VAQty}kg` }))}
              testId="soaking-va-breakup" /></div>
        )}
        <div className="row">
          <div className="col-md-4 mb-3"><label className="form-label">Tub Number</label>
            <input className="form-control" value={v.TubNumber} onChange={(e) => setV((s) => ({ ...s, TubNumber: e.target.value }))} data-testid="soaking-tub-number" /></div>
          <div className="col-md-4 mb-3"><label className="form-label">Chemical</label>
            <select className="form-select" value={v.ChemicalID} onChange={(e) => setV((s) => ({ ...s, ChemicalID: e.target.value }))} data-testid="soaking-chemical">
              <option value="">Select</option>{chemicals.map((c) => <option key={c.ChemID} value={c.ChemID}>{c.ChemicalName}</option>)}
            </select></div>
          <div className="col-md-4 mb-3"><label className="form-label">Time Period (min)</label>
            <input className="form-control" value={v.TimePeriod} onChange={(e) => setV((s) => ({ ...s, TimePeriod: e.target.value }))} data-testid="soaking-time-period" /></div>
        </div>
        <div className="row">
          {(["InCount", "InQuantity", "OutCount", "OutQuantity", "SaltKg", "ChemicalQuantity"] as const).map((k) => (
            <div className="col-md-4 mb-3" key={k}><label className="form-label">{k}</label>
              <input className="form-control" value={(v as any)[k]} onChange={(e) => setV((s) => ({ ...s, [k]: e.target.value }))} data-testid={`soaking-${k.toLowerCase()}`} /></div>
          ))}
        </div>
        <div className="mb-3"><label className="form-label">Remarks</label>
          <input className="form-control" value={v.Remarks} onChange={(e) => setV((s) => ({ ...s, Remarks: e.target.value }))} data-testid="soaking-remarks" /></div>
        {err && <Alert kind="danger" testId="soaking-error">{err}</Alert>}
        <button className="btn btn-primary" type="submit" disabled={saving} data-testid="soaking-submit">{saving ? "Saving..." : "Save"}</button>
      </form>
    </Modal>
  );
};

/** Port of v3/Production/soaking-trackid.php — consumes a Value-Addition breakup's balance into
 * ProdSoakingDetails (Tub Number, Salt/Chemical, In/Out count+qty), guarded by a live "Weight
 * Exceeds" balance check (VA output vs already-soaked total). */
export default function SoakingPage() {
  const [params, setParams] = useSearchParams();
  const arrivalDate = params.get("ArrivalDate") || today();
  const plantId = params.get("PlantID") || "";
  const trackId = params.get("TrackID") || "";
  const qc = useQueryClient();

  const dd = useQuery({ queryKey: ["prod", "dropdowns"], queryFn: prodDropdownsApi.get, staleTime: 5 * 60_000 });
  const tracks = useQuery({ queryKey: ["prod", "tracks", arrivalDate, plantId], enabled: !!(arrivalDate && plantId),
    queryFn: () => productionTracksApi.list({ ArrivalDate: arrivalDate, PlantID: plantId, Type: "Soaking" }) });
  const detail = useQuery({ queryKey: ["prod", "soaking", trackId, plantId], enabled: !!(trackId && plantId),
    queryFn: () => soakingApi.get({ TrackID: trackId, PlantID: plantId }) });

  const plants = useMemo(() => toOptions(dd.data?.Data?.Plant, (p) => p.ProcessingStationID, (p) => p.StationName), [dd.data]);
  const vaBreakups = detail.data?.VaDetails?.VaData || [];
  const rows = detail.data?.SoakingDetails?.SoakingData || [];
  const totals = detail.data?.SoakingDetails?.Totals;
  const [showNew, setShowNew] = useState(false); const [editRow, setEditRow] = useState<SoakingRow | null>(null);

  const del = async (r: SoakingRow) => {
    if (!window.confirm("Delete this Soaking entry?")) return;
    await soakingApi.delete({ BreakUpID: r.BreakUpID, TrackID: trackId, PlantID: plantId });
    qc.invalidateQueries({ queryKey: ["prod", "soaking", trackId, plantId] });
  };

  return (
    <>
      <PageHeader title="Soaking" location="Production" />
      <div className="card mb-3"><div className="card-body">
        <div className="row">
          <div className="col-md-3 mb-3"><label className="form-label">Arrival Date</label>
            <input className="form-control" type="date" value={arrivalDate} onChange={(e) => setParams((p) => { p.set("ArrivalDate", e.target.value); return p; })} data-testid="soaking-arrival-date" /></div>
          <div className="col-md-3 mb-3"><label className="form-label">Plant</label>
            <ErpSelect id="soaking-plant" value={plantId} onChange={(v) => setParams((p) => { p.set("PlantID", v); return p; })} options={plants} placeholder="Select Plant" testId="soaking-plant" /></div>
          <div className="col-md-4 mb-3"><label className="form-label">Track</label>
            <ErpSelect id="soaking-track" value={trackId} onChange={(v) => setParams((p) => { p.set("TrackID", v); return p; })}
              options={(tracks.data?.Data || []).map((t) => ({ value: String(t.TrackID), label: t.TrackCode }))}
              placeholder="Select Track" testId="soaking-track" /></div>
        </div>
      </div></div>

      {trackId && (
        <div className="card"><div className="card-body">
          <div className="d-flex justify-content-between mb-3"><h6>Soaking Entries</h6>
            <button className="btn btn-sm btn-success" data-testid="soaking-add-new" onClick={() => setShowNew(true)}>+ New Soaking Entry</button></div>
          <ErpTable<SoakingRow> testId="soaking-table" loading={detail.isLoading} error={detail.isError ? errorText(detail.error) : null}
            rows={rows} columns={[{ key: "LotNo", title: "Lot No" }, { key: "Variety", title: "Variety" }, { key: "VAGrade", title: "Grade" },
              { key: "TubNumber", title: "Tub" }, { key: "SoakInCount", title: "In Count" }, { key: "SoakInQty", title: "In Qty" },
              { key: "ChemicalName", title: "Chemical" },
              { key: "Action", title: "Action", render: (r) => (
                <div className="d-flex gap-1">
                  <button className="btn btn-sm btn-outline-primary" data-testid={`soaking-edit-${r.BreakUpID}`} onClick={() => setEditRow(r)}>Edit</button>
                  <button className="btn btn-sm btn-outline-danger" data-testid={`soaking-delete-${r.BreakUpID}`} onClick={() => del(r)}>Delete</button>
                </div>
              ) }]}
            footer={totals && <tr><td colSpan={4}><strong>Total</strong></td><td>{totals.TotalSoakInQty}</td><td colSpan={2} /></tr>} />
        </div></div>
      )}
      {(showNew || editRow) && <NewSoakingModal trackId={trackId} plantId={plantId} vaBreakups={vaBreakups} editRow={editRow || undefined}
        onClose={() => { setShowNew(false); setEditRow(null); }}
        onSaved={() => { setShowNew(false); setEditRow(null); qc.invalidateQueries({ queryKey: ["prod", "soaking", trackId, plantId] }); }} />}
    </>
  );
}
